import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { GenerationJob } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import {
	FeedCompaniesArgs,
	useLazyGetFeedCompaniesQuery,
} from "@/pages/Private/redux/feed/feed.api";
import {
	FeedPanelContextView,
	feedSelector,
	jumpToFeedCompany,
	setFeedSelectedPerson,
} from "@/pages/Private/redux/feed/feed.slice";

import { feedCardKey, feedIdentity } from "./feedCardKey";

interface FeedQueueJump {
	/**
	 * Navigate to the card holding the job's lead: page to it if needed, select it, point it at
	 * the job's person and (via the pending jump) scroll it into view. Resolves `false` when the
	 * lead has no reachable card — the caller falls back to opening the panel from the job.
	 */
	jumpToJobCard: (job: GenerationJob, contextView?: FeedPanelContextView) => Promise<boolean>;
}

/**
 * Turns an AI queue-pill row into feed navigation (PREC-3712): clicking a lead selects that
 * lead's *card*, which is what points the docked panel at the lead — instead of swapping the
 * panel's prospect out from under a card the user is still looking at.
 *
 * A lead whose card is on the page in view resolves from the cards already in the slice. One
 * whose card is elsewhere in the result set costs a `/feed/companies` lookup: that endpoint
 * carries each company's page under the current tab/sort/filters, which is exactly what the
 * pending jump needs to land. Everything after that — paging, selecting, scrolling, flashing —
 * is the shared jump-to-company machinery (D3).
 */
export const useFeedQueueJump = ({
	companiesArgs,
}: {
	/** The current feed query's tab/sort/filters — the companies lookup must agree with it. */
	companiesArgs: FeedCompaniesArgs;
}): FeedQueueJump => {
	const dispatch = useAppDispatch();
	const { cards, page } = useAppSelector(feedSelector);
	const [triggerGetFeedCompanies] = useLazyGetFeedCompaniesQuery();

	/** The job's card among the cards in view, matched on the lead's prospect. */
	const findCardOnPage = useCallback(
		(job: GenerationJob) =>
			cards.find((card) => card.people.some((person) => person.prospectId === job.prospectId)) ??
			null,
		[cards]
	);

	/**
	 * The job's card elsewhere in the result set, with the page it lives on. Needs the lead's
	 * tracked company — the card's identity — which is why the job payload carries it.
	 */
	const findCardOffPage = useCallback(
		async (job: GenerationJob): Promise<{ cardId: number; page: number } | null> => {
			if (job.trackerOrganizationId === null) {
				return null;
			}

			try {
				// Never served from cache: an entry's `page` is only true for the result set it
				// came from, and a stale one lands the jump on a page without the card — which
				// spends the target silently and looks exactly like the bug this fixes.
				const { data } = await triggerGetFeedCompanies(companiesArgs, false);
				const entry = data?.data.find(
					(company) => company.trackerOrganizationId === job.trackerOrganizationId
				);

				return entry
					? { cardId: feedIdentity(entry.runId, entry.trackerOrganizationId), page: entry.page }
					: null;
			} catch {
				return null;
			}
		},
		[companiesArgs, triggerGetFeedCompanies]
	);

	const jumpToJobCard = useCallback(
		async (job: GenerationJob, contextView?: FeedPanelContextView): Promise<boolean> => {
			const cardOnPage = findCardOnPage(job);
			// A card in view still goes through the jump: same page, so it scrolls and flashes
			// without a refetch.
			const target = cardOnPage
				? { cardId: feedCardKey(cardOnPage), page }
				: await findCardOffPage(job);

			// The lead is on no card of this tab/filter set (or beyond the lookup's reach).
			if (!target) {
				return false;
			}

			// Filed before the card lands — the switch is keyed by card, so a card arriving from a
			// later page already knows which of its people to show.
			dispatch(setFeedSelectedPerson({ runId: target.cardId, listPersonId: job.listPersonId }));
			dispatch(jumpToFeedCompany({ page: target.page, cardId: target.cardId, contextView }));

			return true;
		},
		[dispatch, findCardOnPage, findCardOffPage, page]
	);

	return { jumpToJobCard };
};
